import { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabase';
import { capture, captureLumensEarned } from '../lib/analytics';
import { T, TIER1_LIST, getTier2, ZERO_COMMENT_PROMPTS, getDiscussionPrompts, LUMENS_ENABLED } from '../lib/constants';
import { timeAgo } from '../lib/utils';
import { useWindowSize } from '../lib/useWindowSize';
import Av from '../components/Av';
import Btn from '../components/Btn';
import FollowBtn from '../components/FollowBtn';
import SafeHtml from '../components/SafeHtml';
import FilePreview from '../components/FilePreview';
import MultiImagePreview from '../components/MultiImagePreview';
import PaperPreview from '../components/PaperPreview';
import RichTextEditor from '../components/RichTextEditor';
import LinkPreview, { extractFirstUrl } from '../components/LinkPreview';
import Linkify from '../components/Linkify';
import MentionAutocomplete from '../components/MentionAutocomplete';
import { detectActiveMention, MentionAndLinkify, notifyMentioned, mentionsToPlainText } from '../lib/mentionUtils';
import { htmlToPlain } from '../lib/htmlUtils';
import ShareModal from '../components/ShareModal';
import ReportModal from '../components/ReportModal';
import LikersModal from './LikersModal';

const TRUNCATE_CHAR_THRESHOLD = 180;
// Show 3 lines fully + the top half of line 4 fading into the bg. The
// gradient overlay below covers that half-line so the cut feels soft
// rather than chopping mid-sentence.
const TRUNCATE_LINE_HEIGHT    = 3.5;
// Approx px per line in SafeHtml (font-size 13 × line-height 1.8).
const TRUNCATE_PX_PER_LINE    = 23.4;
const TRUNCATE_FADE_PX        = 14;
const DEEPDIVE_MIN_WORDS      = 50;

// Awards Lumens for a comment: 2 to the commenter (creation), and 5 to the
// post owner the first time each commenter comments on this post (engagement).
// Self-comments earn no engagement Lumens. Best-effort — failures swallowed.
async function awardLumensForComment(post, commenterId, commenterPrevLumens) {
  if (!LUMENS_ENABLED) return;
  try {
    supabase.rpc('award_lumens', {
      p_user_id:  commenterId,
      p_amount:   2,
      p_reason:   'comment_posted',
      p_category: 'creation',
      p_meta:     { post_id: post.id },
    }).then(() => {}, () => {});
    captureLumensEarned({ reason: 'comment_posted', amount: 2, meta: { post_id: post.id }, prevLumens: commenterPrevLumens });

    if (post?.user_id && post.user_id !== commenterId) {
      const { count } = await supabase
        .from('comments')
        .select('*', { count: 'exact', head: true })
        .eq('post_id',  post.id)
        .eq('user_id',  commenterId);
      // After the insert, count === 1 means this is the commenter's first
      // comment on this post → first-time engagement, award the post owner.
      if (count === 1) {
        supabase.rpc('award_lumens', {
          p_user_id:  post.user_id,
          p_amount:   10,
          p_reason:   'comment_received',
          p_category: 'engagement',
          p_meta:     { post_id: post.id, actor_id: commenterId },
        }).then(() => {}, () => {});
        captureLumensEarned({ reason: 'comment_received', amount: 10, meta: { post_id: post.id, actor_id: commenterId, recipient_id: post.user_id } });
      }

      // Discussion threshold: when distinct commenter count just hits 3,
      // award the post owner +50 Lumens (recognition). One-time per post —
      // dedup via lumen_transactions row check on (post.user_id, reason,
      // meta.post_id).
      const { data: distinct } = await supabase
        .from('comments')
        .select('user_id')
        .eq('post_id', post.id);
      const uniq = new Set((distinct || []).map(r => r.user_id));
      if (uniq.size === 3) {
        const { data: existing } = await supabase
          .from('lumen_transactions')
          .select('id')
          .eq('user_id', post.user_id)
          .eq('reason',  'discussion_threshold')
          .filter('meta->>post_id', 'eq', post.id)
          .limit(1);
        if (!existing?.length) {
          supabase.rpc('award_lumens', {
            p_user_id:  post.user_id,
            p_amount:   50,
            p_reason:   'discussion_threshold',
            p_category: 'recognition',
            p_meta:     { post_id: post.id },
          }).then(() => {}, () => {});
          captureLumensEarned({ reason: 'discussion_threshold', amount: 50, meta: { post_id: post.id, recipient_id: post.user_id } });
        }
      }
    }
  } catch {
    // Swallow any sync/async failure so the comment flow always completes.
  }
}

// Inserts a new_comment notification for the post owner, deduped so a flurry
// of comments while the previous one is still unread doesn't pile up.
async function notifyPostOwnerOfComment(post, commenterId) {
  if (!post?.user_id || post.user_id === commenterId) return;
  const { data: existing } = await supabase
    .from('notifications')
    .select('id')
    .eq('user_id',    post.user_id)
    .eq('notif_type', 'new_comment')
    .eq('target_id',  post.id)
    .eq('read',       false)
    .maybeSingle();
  if (existing) return;
  await supabase.from('notifications').insert({
    user_id:    post.user_id,
    actor_id:   commenterId,
    notif_type: 'new_comment',
    target_id:  post.id,
    read:       false,
  });
}

function GranularTags({ tags, onTagClick }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? tags : tags.slice(0, 3);
  const hidden  = tags.length - 3;
  return (
    <div style={{display:'flex', gap:4, flexWrap:'wrap', marginTop:3}}>
      {visible.map(tag => (
        <span key={tag} onClick={() => onTagClick && onTagClick(tag)}
          style={{fontSize:10, color:T.mu, padding:'1px 7px', borderRadius:20, background:T.s2, border:`1px solid ${T.bdr}`, cursor:onTagClick?'pointer':'default'}}>
          {tag}
        </span>
      ))}
      {!expanded && hidden > 0 && (
        <span onClick={() => setExpanded(true)}
          style={{fontSize:10, color:T.v, padding:'1px 7px', borderRadius:20, background:T.v2, border:`1px solid rgba(108,99,255,.15)`, cursor:'pointer', fontWeight:600}}>
          +{hidden} more
        </span>
      )}
    </div>
  );
}

export default function PostCard({
  post, currentUserId, currentProfile,
  onRefresh, onViewUser, onUnfollow, onViewPaper, hidePaperDetails,
  onTagClick, onViewGroup, onViewProject, onEditPost,
  availableFolders = null,
    // When set (project context only), the post owner gets a "Move to
    // folder" submenu in the ⋯ menu listing these folders. Each entry
    // is `{ id, name }`. Pass null/undefined to suppress.
  canPin = false,
    // True when the viewer is the group/project context owner. Adds a
    // 📌 Pin / Unpin item to the ⋯ menu (in addition to the post-owner
    // items, if the viewer is also the post author). Feed context
    // ignores this prop.
  hideContextBanner = false,
    // Suppresses the "🔬 Shared from [Group]" / "📁 In project: [Name]"
    // banner. Set by GroupFeed / ProjectFeed when the viewer is already
    // inside the post's native context (the banner would just be
    // redundant). The banner still renders elsewhere — public-feed
    // overlay, reposts, /s/:postId, profile lists, etc.
  isSaved = false, onSaveToggled,
}) {
  const { isMobile } = useWindowSize();
  const ctx = post.context_kind || 'feed';

  const [liked,setLiked]             = useState(post.user_liked||false);
  const [likeCount,setLikeCount]     = useState(parseInt(post.like_count)||0);
  const [reposted,setReposted]       = useState(post.user_reposted||false);
  const [repostCount,setRepostCount] = useState(parseInt(post.repost_count)||0);
  const [reposting,setReposting]     = useState(false);
  const [saving,setSaving]           = useState(false);
  const [saved,setSaved]             = useState(isSaved);
  useEffect(() => { setSaved(isSaved); }, [isSaved]);
  const [menuOpen,setMenuOpen]       = useState(false);
  const [showFolderMove, setShowFolderMove] = useState(false);
  const [editing,setEditing]       = useState(false);
  const [editText,setEditText]     = useState(post.content||'');
  const [editSaving,setEditSaving] = useState(false);
  const [deleted,setDeleted]       = useState(false);
  const [confirmDelete,setConfirmDelete] = useState(false);
  const [showComments,setShowComments]   = useState(false);
  const [showShare,setShowShare]         = useState(false);
  const [showReport,setShowReport]       = useState(false);
  const [showLikers,setShowLikers]       = useState(false);
  const [expanded,setExpanded]           = useState(false);

  const [comments,  setComments]   = useState([]);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingContent,   setEditingContent]   = useState('');
  const [editingSaving,    setEditingSaving]    = useState(false);
  const [editingError,     setEditingError]     = useState('');
  // @-mention dropdown state — shared across the new-comment input and
  // the inline edit textarea. Only one can be active at a time.
  const [mentionState, setMentionState] = useState(null); // { target: 'new'|'edit', query, start, caret, top, left }
  const [commLoaded,setCommLoaded] = useState(false);
  const [commLoading,setCommLoading]=useState(false);
  const [commText,  setCommText]   = useState('');
  const [commSaving,setCommSaving] = useState(false);
  const [commCount, setCommCount]  = useState(parseInt(post.comment_count)||0);
  const commInputRef = useRef(null);

  const [editingTags,  setEditingTags]  = useState(false);
  const [editTier1,    setEditTier1]    = useState(post.tier1 || '');
  const [editTier2,    setEditTier2]    = useState(post.tier2 || []);
  // Raw editable string. Parsed into an array only on save — fixes the
  // commas-get-eaten bug from controlling the input with editTags.join().
  const [editTagsText, setEditTagsText] = useState((post.tags || []).join(', '));

  const [abstractExpanded, setAbstractExpanded] = useState(false);
  // Top comment + commenter avatars arrive on `post` directly via
  // posts_with_meta (top_comment / commenter_avatars JSONB) — see
  // migration_post_aggregates_denorm.sql. PostCard used to issue two
  // queries per post on mount; that was 80 round-trips per 40-row feed.
  // localTopComment lets submitQuickReply show its own reply instantly
  // without waiting for the parent to refetch the view.
  const [localTopComment, setLocalTopComment] = useState(null);
  const topComment       = localTopComment ?? post.top_comment ?? null;
  const commenterAvatars = post.commenter_avatars || [];
  const [showReplyBox,     setShowReplyBox]     = useState(false);
  const [replyText,        setReplyText]        = useState('');
  const [promptIndex,      setPromptIndex]      = useState(() => Math.floor(Math.random() * 10));

  const isOwner = currentUserId && currentUserId === post.user_id;

  // Milestone post — special celebration card, no social actions.
  // Kept as-is from the original feed PostCard.
  if (post.post_type === 'milestone') {
    const slug = post.author_slug || currentProfile?.profile_slug;
    const cardUrl = slug ? `${window.location.origin}/c/${slug}` : null;
    return (
      <div style={{
        background: 'linear-gradient(135deg, #eeecff 0%, #f0f9ff 100%)',
        border: `1.5px solid rgba(108,99,255,.25)`,
        borderRadius: 14, overflow: 'hidden',
        boxShadow: '0 2px 12px rgba(108,99,255,.1)',
      }}>
        <div style={{ height: 4, background: 'linear-gradient(90deg, #667eea, #764ba2, #f093fb)' }}/>
        <div style={{ padding: '20px 22px' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>🎉</div>
          <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 18, marginBottom: 6, color: T.text }}>
            Profile complete!
          </div>
          <div style={{ fontSize: 13, color: T.mu, lineHeight: 1.6, marginBottom: 14 }}>
            Your Luminary profile is ready. Share it with colleagues, print your QR code on a poster, or exchange your virtual business card at your next conference.
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
            {slug && (
              <a href={`${window.location.origin}/p/${slug}`} target="_blank" rel="noopener noreferrer"
                style={{
                  padding: '8px 16px', borderRadius: 10, border: 'none',
                  background: 'linear-gradient(135deg, #6c63ff, #764ba2)',
                  color: '#fff', fontSize: 12.5, fontWeight: 700,
                  textDecoration: 'none', display: 'inline-block',
                }}>
                View my profile →
              </a>
            )}
            {cardUrl && (
              <a href={cardUrl} target="_blank" rel="noopener noreferrer"
                style={{
                  padding: '8px 14px', borderRadius: 10,
                  border: `1.5px solid rgba(108,99,255,.3)`, background: 'transparent',
                  color: T.v, fontSize: 12.5, fontWeight: 600,
                  textDecoration: 'none', display: 'inline-block',
                }}>
                🪪 Virtual business card
              </a>
            )}
            {slug && (
              <button
                onClick={() => navigator.clipboard?.writeText(`${window.location.origin}/p/${slug}`)}
                style={{
                  padding: '8px 14px', borderRadius: 10,
                  border: `1.5px solid ${T.bdr}`, background: 'transparent',
                  color: T.mu, fontSize: 12.5, fontWeight: 600,
                  fontFamily: 'inherit', cursor: 'pointer',
                }}>
                📋 Copy profile link
              </button>
            )}
          </div>

          <div style={{
            background: 'rgba(108,99,255,.07)', borderRadius: 9,
            padding: '9px 12px', fontSize: 12, color: T.v, lineHeight: 1.6,
          }}>
            💡 <strong>Tip:</strong> Your QR code is in your profile under <em>Share</em>. Add it to a poster or slide so colleagues can find you instantly.
          </div>

          <div style={{ marginTop: 10, fontSize: 11, color: T.mu }}>
            Only visible to you · {timeAgo(post.created_at)}
          </div>
        </div>
      </div>
    );
  }

  const goToProfile = (userId, slug) => {
    if (onViewUser && userId) { onViewUser(userId); return; }
    if (slug) window.location.href = `/p/${slug}`;
  };

  const toggleRepost = async () => {
    if (!currentUserId || reposting) return;
    setReposting(true);
    const nr = !reposted;
    setReposted(nr); setRepostCount(c => nr ? c+1 : Math.max(0,c-1));
    if (nr) {
      await supabase.from('reposts').insert({user_id:currentUserId, post_id:post.id});
      // Reward the post owner +20 Lumens for the repost (recognition).
      // Skip self-reposts. Best-effort.
      if (LUMENS_ENABLED && post?.user_id && post.user_id !== currentUserId) {
        try {
          supabase.rpc('award_lumens', {
            p_user_id:  post.user_id,
            p_amount:   20,
            p_reason:   'post_reposted',
            p_category: 'recognition',
            p_meta:     { post_id: post.id, actor_id: currentUserId },
          }).then(() => {}, () => {});
          captureLumensEarned({ reason: 'post_reposted', amount: 20, meta: { post_id: post.id, actor_id: currentUserId, recipient_id: post.user_id } });
        } catch {}
      }
    } else {
      await supabase.from('reposts').delete().eq('user_id',currentUserId).eq('post_id',post.id);
    }
    setReposting(false);
  };

  const toggleLike = async () => {
    if(!currentUserId||saving) return;
    setSaving(true);
    const nl=!liked; setLiked(nl); setLikeCount(c=>nl?c+1:c-1);
    if(nl) { await supabase.from('likes').insert({user_id:currentUserId,post_id:post.id}); capture('post_liked'); }
    else await supabase.from('likes').delete().eq('user_id',currentUserId).eq('post_id',post.id);
    setSaving(false);
  };

  const saveEdit = async () => {
    if(!editText.trim()) return;
    setEditSaving(true);
    await supabase.from('posts').update({ content: editText.trim(), edited_at: new Date().toISOString() }).eq('id', post.id);
    setEditSaving(false); setEditing(false); setMenuOpen(false);
    onRefresh && onRefresh();
  };

  const deletePost = async () => {
    await supabase.from('posts').delete().eq('id', post.id);
    setDeleted(true);
    onRefresh && onRefresh();
  };

  const moveToFolder = async (folderId) => {
    // folderId === null means "All posts" (folder-less)
    await supabase.from('posts').update({ folder_id: folderId }).eq('id', post.id);
    setShowFolderMove(false);
    setMenuOpen(false);
    onRefresh && onRefresh();
  };

  const togglePin = async () => {
    // Owner check is enforced server-side in pin_post / unpin_post.
    const fn = post.pinned_at ? 'unpin_post' : 'pin_post';
    await supabase.rpc(fn, { p_post_id: post.id });
    setMenuOpen(false);
    onRefresh && onRefresh();
  };

  const loadComments = async () => {
    if (commLoaded) return;
    setCommLoading(true);
    const { data } = await supabase
      .from('comments')
      .select('*, profiles(name, avatar_color, avatar_url, institution, profile_slug)')
      .eq('post_id', post.id)
      .order('created_at', { ascending: true });
    setComments(data||[]);
    setCommLoaded(true);
    setCommLoading(false);
  };

  const toggleComments = async () => {
    const next = !showComments;
    setShowComments(next);
    if (next) {
      await loadComments();
      setTimeout(() => commInputRef.current?.focus(), 100);
    }
  };

  const submitComment = async () => {
    if (!commText.trim() || !currentUserId || commSaving) return;
    setCommSaving(true);
    const content = commText.trim();
    const { data } = await supabase
      .from('comments')
      .insert({ post_id: post.id, user_id: currentUserId, content })
      .select('*, profiles(name, avatar_color, avatar_url, institution, profile_slug)')
      .single();
    if (data) {
      setComments(c => [...c, data]);
      setCommCount(n => n + 1);
      setCommText('');
      capture('comment_posted');
      await awardLumensForComment(post, currentUserId, currentProfile?.lumens_current_period);
      await notifyPostOwnerOfComment(post, currentUserId);
      await notifyMentioned(supabase, content, false, currentUserId, post.id);
    }
    setCommSaving(false);
  };

  const deleteComment = async (id) => {
    await supabase.from('comments').delete().eq('id', id);
    setComments(c => c.filter(x => x.id !== id));
    setCommCount(n => Math.max(0, n - 1));
  };

  // Mention input handlers — call from each textarea's onInput/onSelect.
  // Detects `@<query>` immediately before the caret and parks dropdown
  // position relative to the textarea. Host's onChange still updates
  // the underlying text state independently.
  const handleMentionInput = (target, textareaEl, value) => {
    const caret = textareaEl?.selectionStart ?? 0;
    const m = detectActiveMention(value, caret);
    if (!m) { setMentionState(null); return; }
    const r = textareaEl.getBoundingClientRect();
    setMentionState({
      target,
      query: m.query,
      start: m.start,
      caret,
      top:  r.bottom + 4,
      left: r.left,
    });
  };
  const insertMentionInComment = (profile) => {
    if (!mentionState) return;
    const slug = profile.profile_slug;
    if (!slug) return;
    // Markdown-style marker so render-time shows the full display name
    // (and so the post + comment surfaces look consistent). Ugly in
    // the textarea while editing — accepted v1 tradeoff.
    const name = profile.name || slug;
    const marker = `@[${name}](${slug}) `;
    if (mentionState.target === 'new') {
      const next = commText.slice(0, mentionState.start) + marker + commText.slice(mentionState.caret);
      setCommText(next);
      const newCaret = mentionState.start + marker.length;
      setTimeout(() => {
        const el = commInputRef.current;
        if (el) { el.focus(); el.setSelectionRange(newCaret, newCaret); }
      }, 0);
    } else if (mentionState.target === 'edit') {
      const next = editingContent.slice(0, mentionState.start) + marker + editingContent.slice(mentionState.caret);
      setEditingContent(next);
    } else if (mentionState.target === 'reply') {
      const next = replyText.slice(0, mentionState.start) + marker + replyText.slice(mentionState.caret);
      setReplyText(next);
    }
    setMentionState(null);
  };

  const startEditComment = (c) => {
    setEditingCommentId(c.id);
    setEditingContent(c.content || '');
    setEditingError('');
  };

  const cancelEditComment = () => {
    setEditingCommentId(null);
    setEditingContent('');
    setEditingError('');
  };

  const saveEditComment = async () => {
    const next = editingContent.trim();
    if (!next || editingSaving) return;
    setEditingSaving(true);
    setEditingError('');
    // Chain .select() so a silent RLS rejection surfaces — same
    // pattern as PubRow.saveEdit. The DB trigger stamps edited_at.
    const { data, error } = await supabase
      .from('comments')
      .update({ content: next })
      .eq('id', editingCommentId)
      .select();
    if (error) {
      console.error('Comment edit error:', error);
      setEditingError('Save failed. Please try again.');
    } else if (!data || data.length === 0) {
      setEditingError('Save blocked by database policy.');
    } else {
      setComments(cs => cs.map(x =>
        x.id === editingCommentId ? { ...x, content: next, edited_at: data[0].edited_at } : x
      ));
      // Mention any newly-added users — dedup against unread prior mention
      // notifs so simple edits don't re-fire.
      await notifyMentioned(supabase, next, false, currentUserId, post.id);
      cancelEditComment();
    }
    setEditingSaving(false);
  };

  const toggleSave = async () => {
    const next = !saved;
    setSaved(next);
    if (next) {
      await supabase.from('saved_posts').insert({ user_id: currentUserId, post_id: post.id });
    } else {
      await supabase.from('saved_posts').delete().eq('user_id', currentUserId).eq('post_id', post.id);
    }
    onSaveToggled?.();
  };

  const saveTagEdits = async () => {
    // Strip leading '#' to match the DB convention (bare tags) — see
    // PostComposer.publish() for the same normalisation on initial save.
    const parsedTags = editTagsText
      .split(',')
      .map(t => t.replace(/^#+/, '').trim())
      .filter(Boolean);
    await supabase.from('posts').update({ tier1: editTier1, tier2: editTier2, tags: parsedTags }).eq('id', post.id);
    setEditingTags(false);
    onRefresh && onRefresh();
  };

  const submitQuickReply = async () => {
    if (!replyText.trim() || !currentUserId) return;
    const text = replyText.trim();
    setReplyText('');
    setShowReplyBox(false);
    setMentionState(null);
    await supabase.from('comments').insert({
      post_id: post.id,
      user_id: currentUserId,
      content: text,
    });
    await notifyMentioned(supabase, text, false, currentUserId, post.id);
    const { data } = await supabase
      .from('comments')
      .select('id, content, created_at, profiles(name, avatar_url, avatar_color)')
      .eq('post_id', post.id)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();
    if (data) setLocalTopComment(data);
    setPromptIndex(i => i + 1);
    await awardLumensForComment(post, currentUserId, currentProfile?.lumens_current_period);
    await notifyPostOwnerOfComment(post, currentUserId);
  };

  const isRelevantToUser = () => {
    if (!currentProfile || !post.comment_count) return false;
    if (post.tier1 && post.tier1 === currentProfile.identity_tier1) return true;
    if (post.tier2?.includes(currentProfile.identity_tier2)) return true;
    const interests = new Set((currentProfile.topic_interests || []).map(t => t.toLowerCase()));
    if ((post.tier2 || []).some(t => interests.has(t.toLowerCase()))) return true;
    return false;
  };

  const getRelevanceLabel = () => {
    const count = post.comment_count;
    const field = post.tier2?.[0] || post.tier1 || 'your field';
    if (count === 1) return `1 researcher in ${field} is discussing this`;
    return `${count} researchers in ${field} are discussing this`;
  };

  if (deleted) return null;

  // Group / project chip hooks
  const groupId      = ctx === 'group'   ? post.context_id : null;
  const projectId    = ctx === 'project' ? post.context_id : null;
  const groupClickable   = !!(groupId   && onViewGroup);
  const projectClickable = !!(projectId && onViewProject);

  const cardBg = post.bg_color || T.w;

  return (
    <div style={{
      borderLeft: post.is_admin_post ? `3px solid ${T.v}` : 'none',
      paddingLeft: post.is_admin_post ? 12 : 0,
      marginLeft: post.is_admin_post ? -12 : 0,
    }}>
    {post.is_admin_post && (
      <div style={{ fontSize: 11, fontWeight: 700, color: T.v, marginBottom: 4, letterSpacing: 0.3, paddingLeft: 2 }}>
        ✦ FROM LUMINARY TEAM
      </div>
    )}
    <div style={{
      background: cardBg,
      border: post.is_deep_dive ? `1.5px solid rgba(108,99,255,.25)` : `1px solid ${T.bdr}`,
      borderLeft: post.is_deep_dive ? `4px solid ${T.v}` : undefined,
      borderRadius: 14,
      overflow: "hidden",
      boxShadow: "0 2px 12px rgba(108,99,255,.07)",
    }}>

      {/* Group context banner */}
      {ctx === 'group' && post.group_name && !hideContextBanner && (
        <div
          onClick={groupClickable ? (e)=>{ e.stopPropagation(); onViewGroup(groupId); } : undefined}
          style={{display:"flex",alignItems:"center",gap:5,padding:"6px 14px",background:T.v2,borderBottom:`1px solid ${T.bdr}`,fontSize:11.5,color:T.mu,fontWeight:600,cursor:groupClickable?"pointer":"default"}}
        >
          🔬 Shared from <span style={{color:T.v,fontWeight:700}}>{post.group_name}</span>
          {groupClickable && <span style={{marginLeft:"auto",fontSize:10.5,color:T.v}}>Open group →</span>}
        </div>
      )}

      {/* Project context banner */}
      {ctx === 'project' && post.project_name && !hideContextBanner && (
        <div
          onClick={projectClickable ? (e)=>{ e.stopPropagation(); onViewProject(projectId); } : undefined}
          style={{display:"flex",alignItems:"center",gap:5,padding:"6px 14px",background:T.v2,borderBottom:`1px solid ${T.bdr}`,fontSize:11.5,color:T.mu,fontWeight:600,cursor:projectClickable?"pointer":"default"}}
        >
          {post.project_icon || '📁'} In project: <span style={{color:T.v,fontWeight:700}}>{post.project_name}</span>
          {projectClickable && <span style={{marginLeft:"auto",fontSize:10.5,color:T.v}}>Open project →</span>}
        </div>
      )}

      {/* Repost banner */}
      {post.isRepost&&(
        <div style={{display:"flex",alignItems:"center",gap:6,padding:"6px 14px",background:T.s2,borderBottom:`1px solid ${T.bdr}`,fontSize:11.5,color:T.mu,fontWeight:600}}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.mu} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/>
          </svg>
          <span onClick={()=>goToProfile(post.reposter_id,post.reposter_slug)} style={{cursor:"pointer"}}>
            {post.reposter_name||"Someone"} reposted
          </span>
        </div>
      )}

      <div style={{padding: isMobile ? '10px 12px' : 16, position:"relative"}}>

        <div style={{display:"flex",alignItems:"flex-start",gap:10,marginBottom:10}}>
          <div onClick={()=>goToProfile(post.user_id,post.author_slug)} style={{cursor:"pointer",flexShrink:0}}>
            <Av color={post.author_avatar||"me"} size={38} name={post.author_name} url={post.author_avatar_url||""}/>
          </div>
          <div style={{flex:1}}>
            <div style={{fontWeight:700,fontSize:12.5,display:"flex",alignItems:"center",gap:5,flexWrap:"wrap"}}>
              <span onClick={()=>goToProfile(post.user_id,post.author_slug)} style={{cursor:"pointer",color:T.v}}>{post.author_name||"Researcher"}</span>
              {post.author_identity_tier2&&(
                <span style={{fontSize:10,fontWeight:600,padding:'2px 8px',borderRadius:20,background:T.v2,color:T.v,border:`1px solid rgba(108,99,255,.2)`}}>
                  {post.author_identity_tier2}
                </span>
              )}
            </div>
            <div style={{fontSize:10.5,color:T.mu}}>
              {post.author_institution&&`${post.author_institution} · `}{timeAgo(post.created_at)}
              {post.edited_at&&<span style={{color:T.mu,fontSize:10}}> · edited</span>}
            </div>
            {currentProfile?.is_admin && post.report_count > 0 && (
              <div style={{display:'flex',gap:4,marginTop:3,flexWrap:'wrap'}}>
                <span style={{display:'inline-flex',alignItems:'center',gap:4,fontSize:11,fontWeight:700,color:T.am,background:T.am2,padding:'2px 8px',borderRadius:20}}>
                  🚩 {post.report_count} report{post.report_count > 1 ? 's' : ''}
                </span>
              </div>
            )}
          </div>

          {!isOwner&&post.user_id&&(
            <FollowBtn targetType="user" targetId={post.user_id} currentUserId={currentUserId}
              onToggle={nowFollowing => { if (!nowFollowing) onUnfollow?.(post.user_id); }}/>
          )}

          {currentUserId&&!isOwner&&(
            <div style={{position:"relative"}}>
              <button onClick={()=>setMenuOpen(!menuOpen)}
                style={{width:28,height:28,borderRadius:"50%",border:"none",background:menuOpen?T.s2:"transparent",cursor:"pointer",fontSize:16,color:T.mu,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                ···
              </button>
              {menuOpen&&(
                <>
                  <div onClick={()=>setMenuOpen(false)} style={{position:"fixed",inset:0,zIndex:9}}/>
                  <div style={{position:"absolute",right:0,top:32,background:T.w,border:`1px solid ${T.bdr}`,borderRadius:11,boxShadow:"0 4px 20px rgba(0,0,0,.12)",zIndex:10,minWidth:160,overflow:"hidden"}}>
                    {canPin && (ctx === 'group' || ctx === 'project') && (
                      <button onClick={togglePin} style={menuItemStyle(T.text)}>
                        {post.pinned_at ? '📌 Unpin from top' : '📌 Pin to top'}
                      </button>
                    )}
                    <button onClick={()=>{setShowReport(true);setMenuOpen(false);}} style={menuItemStyle(T.text)}>🚩 Report</button>
                  </div>
                </>
              )}
            </div>
          )}

          {isOwner&&(
            <div style={{position:"relative"}}>
              <button onClick={()=>setMenuOpen(!menuOpen)}
                style={{width:28,height:28,borderRadius:"50%",border:"none",background:menuOpen?T.s2:"transparent",cursor:"pointer",fontSize:16,color:T.mu,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                ···
              </button>
              {menuOpen&&(
                <>
                  <div onClick={()=>{setMenuOpen(false);setConfirmDelete(false);setShowFolderMove(false);}} style={{position:"fixed",inset:0,zIndex:9}}/>
                  <div style={{position:"absolute",right:0,top:32,background:T.w,border:`1px solid ${T.bdr}`,borderRadius:11,boxShadow:"0 4px 20px rgba(0,0,0,.12)",zIndex:10,minWidth:180,overflow:"hidden"}}>
                    {!confirmDelete && !showFolderMove ? (
                      <>
                        <button onClick={()=>{
                          // Deep dives open the full composer (cover, image insert, title, etc.).
                          // Regular posts use the inline editor.
                          if (post.is_deep_dive && onEditPost) { onEditPost(post); }
                          else                                  { setEditing(true); }
                          setMenuOpen(false);
                        }} style={menuItemStyle(T.text)}>✏️ Edit post</button>
                        <button onClick={()=>{setEditingTags(true);setMenuOpen(false);}} style={menuItemStyle(T.text)}>🏷️ Edit tags</button>
                        {/* Project context only: surface the folder picker */}
                        {availableFolders && post.context_kind === 'project' && (
                          <button onClick={()=>setShowFolderMove(true)} style={menuItemStyle(T.text)}>
                            📁 Move to folder…
                          </button>
                        )}
                        {/* Group/project owner only — server enforces auth too */}
                        {canPin && (ctx === 'group' || ctx === 'project') && (
                          <button onClick={togglePin} style={menuItemStyle(T.text)}>
                            {post.pinned_at ? '📌 Unpin from top' : '📌 Pin to top'}
                          </button>
                        )}
                        <div style={{height:1,background:T.bdr,margin:"0 10px"}}/>
                        <button onClick={()=>setConfirmDelete(true)} style={menuItemStyle(T.ro)}>🗑️ Delete post</button>
                      </>
                    ) : showFolderMove ? (
                      <div>
                        <div style={{padding:"10px 14px 6px",fontSize:11,fontWeight:700,color:T.mu,letterSpacing:0.3,textTransform:"uppercase",borderBottom:`1px solid ${T.bdr}`}}>
                          Move to folder
                        </div>
                        <button onClick={()=>moveToFolder(null)}
                          style={{...menuItemStyle(post.folder_id ? T.text : T.v),fontWeight:post.folder_id ? 500 : 700}}>
                          📋 All posts {!post.folder_id && '✓'}
                        </button>
                        {availableFolders.map(f => (
                          <button key={f.id} onClick={()=>moveToFolder(f.id)}
                            style={{...menuItemStyle(post.folder_id === f.id ? T.v : T.text),fontWeight:post.folder_id === f.id ? 700 : 500}}>
                            📁 {f.name} {post.folder_id === f.id && '✓'}
                          </button>
                        ))}
                        <div style={{height:1,background:T.bdr,margin:"0 10px"}}/>
                        <button onClick={()=>setShowFolderMove(false)}
                          style={menuItemStyle(T.mu)}>← Back</button>
                      </div>
                    ) : (
                      <div style={{padding:"14px 16px"}}>
                        <div style={{fontSize:13,fontWeight:700,marginBottom:4,color:T.text}}>Delete this post?</div>
                        <div style={{fontSize:12,color:T.mu,marginBottom:12}}>This cannot be undone.</div>
                        <div style={{display:"flex",gap:8}}>
                          <button onClick={()=>{setConfirmDelete(false);setMenuOpen(false);}} style={{flex:1,padding:"7px",border:`1px solid ${T.bdr}`,borderRadius:9,background:T.w,cursor:"pointer",fontSize:12,fontFamily:"inherit",fontWeight:600,color:T.mu}}>Cancel</button>
                          <button onClick={deletePost} style={{flex:1,padding:"7px",border:"none",borderRadius:9,background:T.ro,cursor:"pointer",fontSize:12,fontFamily:"inherit",fontWeight:700,color:"#fff"}}>Delete</button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Post type / pinned badges */}
        {(post.is_deep_dive || post.pinned_at) && (
          <div style={{marginBottom: 8, display: 'flex', gap: 6, flexWrap: 'wrap'}}>
            {post.pinned_at && (
              <span style={{
                fontSize: 10.5, fontWeight: 700,
                padding: '2px 9px', borderRadius: 20,
                background: T.am2, color: T.am,
                border: `1px solid rgba(245,158,11,.25)`,
              }}>
                📌 Pinned
              </span>
            )}
            {post.is_deep_dive && (
              <span style={{
                fontSize: 10.5, fontWeight: 700,
                padding: '2px 9px', borderRadius: 20,
                background: T.v2, color: T.v,
                border: `1px solid rgba(108,99,255,.2)`,
              }}>
                🔬 Deep Dive
              </span>
            )}
          </div>
        )}

        {editing ? (
          <div style={{marginBottom:12}}>
            <RichTextEditor value={editText} onChange={setEditText} placeholder="Edit your post..." minHeight={80}/>
            <div style={{display:"flex",gap:8,marginTop:8,justifyContent:"flex-end"}}>
              <button onClick={()=>{setEditing(false);setEditText(post.content||'');}} style={{padding:"6px 14px",borderRadius:20,border:`1px solid ${T.bdr}`,background:T.w,cursor:"pointer",fontSize:12,fontFamily:"inherit",fontWeight:600,color:T.mu}}>Cancel</button>
              <button onClick={saveEdit} disabled={editSaving||!editText.trim()} style={{padding:"6px 16px",borderRadius:20,border:"none",background:T.v,cursor:"pointer",fontSize:12,fontFamily:"inherit",fontWeight:700,color:"#fff",opacity:(editSaving||!editText.trim())?.6:1}}>{editSaving?"Saving...":"Save changes"}</button>
            </div>
          </div>
        ) : (() => {
          // Use htmlToPlain so HTML entities (&nbsp;, &amp;, etc.) are
          // decoded — pasted-from-web HTML often carries them and a plain
          // tag-strip leaves the entity codes as literal text in the preview.
          const plain = htmlToPlain(post.content || '');
          const wordCount = plain ? plain.split(/\s+/).filter(Boolean).length : 0;
          const isDeepDive = post.is_deep_dive === true && wordCount >= DEEPDIVE_MIN_WORDS;

          // Deep dive — article card replaces the content render entirely.
          if (isDeepDive) {
            const explicitTitle = (post.deep_dive_title || '').trim();
            const lines = plain.split('\n').filter(l => l.trim());
            const extractedTitle = !explicitTitle && lines[0] && lines[0].length < 120 ? lines[0] : null;
            const title = explicitTitle || extractedTitle || null;
            const previewStart = extractedTitle ? plain.indexOf('\n') + 1 : 0;
            const remaining    = plain.slice(previewStart).trim();
            const previewText  = remaining.slice(0, 325).trim();
            const preview      = previewText.length < remaining.length ? previewText + '…' : previewText;
            const readMins     = Math.max(1, Math.round(wordCount / 200));
            const coverUrl     = post.deep_dive_cover_url || '';
            return (
              <div
                onClick={() => {
                  // Stash the in-app context so the back button on /s/:postId
                  // returns the user to the group/project they were browsing
                  // instead of dumping them onto the general feed.
                  if (currentUserId) {
                    let ret = null;
                    if (post.context_kind === 'group') {
                      ret = { screen: 'groups', activeGroupId: post.context_id };
                    } else if (post.context_kind === 'project') {
                      ret = post.project_group_id
                        ? { screen: 'groups', activeGroupId: post.project_group_id, initialProjectId: post.context_id }
                        : { screen: 'projects', initialProjectId: post.context_id };
                    }
                    if (ret) sessionStorage.setItem('post_return_to', JSON.stringify(ret));
                    else sessionStorage.removeItem('post_return_to');
                  }
                  window.location.href = `/s/${post.id}`;
                }}
                style={{
                  background: T.s2, border: `1px solid ${T.bdr}`, borderRadius: 10,
                  cursor: 'pointer', marginTop: 4, overflow: 'hidden',
                }}
              >
                {coverUrl && (
                  <img src={coverUrl} alt=""
                    style={{
                      display:'block', width:'100%', height:200,
                      objectFit:'cover',
                      objectPosition: post.deep_dive_cover_position || '50% 50%',
                    }}/>
                )}
                <div style={{ padding:'14px 16px' }}>
                  {title && (
                    <div style={{
                      fontFamily:"'DM Serif Display', serif", fontSize:17,
                      color:T.text, lineHeight:1.35, marginBottom:6, fontWeight:400,
                    }}>
                      {title}
                    </div>
                  )}
                  {preview && (
                    <div style={{ fontSize:13.5, color:T.mu, lineHeight:1.6, marginBottom:10 }}>
                      {preview}
                    </div>
                  )}
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                    <span style={{ fontSize:12, color:T.mu }}>{readMins} min read</span>
                    <span style={{ fontSize:13, fontWeight:600, color:T.v }}>Continue reading →</span>
                  </div>
                </div>
              </div>
            );
          }

          // Any post that lands here gets clamped — text, paper
          // commentary, and text-with-attachment (image/video/audio/pdf/
          // file all set post_type to the attachment kind, so a
          // post_type gate would let those slip through full-bleed).
          // Admin posts and deep dives have their own treatments
          // (✦ header / article preview card) and reach this block via
          // earlier branches, so an !is_admin_post guard is enough.
          // Triggers on either character length OR line breaks (a short
          // post with multiple newlines can still exceed 3 visible lines).
          const newlineCount = (plain.match(/\n/g) || []).length;
          const needsTruncation =
            !post.is_admin_post &&
            (plain.length > TRUNCATE_CHAR_THRESHOLD || newlineCount > 2);
          const url = post.post_type === 'text' ? extractFirstUrl(post.content || '') : null;

          return (
            <>
              {post.content && (needsTruncation && !expanded ? (
                <div style={{ position:'relative' }}>
                  <div style={{
                    maxHeight: `${TRUNCATE_LINE_HEIGHT * TRUNCATE_PX_PER_LINE}px`,
                    overflow:'hidden',
                  }}>
                    <SafeHtml html={post.content} tags={post.tags} onTagClick={onTagClick}/>
                  </div>
                  <div style={{
                    position:'absolute', bottom:0, left:0, right:0, height:TRUNCATE_FADE_PX,
                    background:`linear-gradient(to bottom, transparent, ${cardBg})`,
                    pointerEvents:'none',
                  }}/>
                </div>
              ) : (
                <SafeHtml html={post.content} tags={post.tags} onTagClick={onTagClick}/>
              ))}
              {needsTruncation && (
                <button
                  onClick={e => { e.stopPropagation(); setExpanded(v => !v); }}
                  style={{
                    background:'none', border:'none', padding:'4px 0 0',
                    cursor:'pointer', fontSize:13.5, fontWeight:600, color:T.v,
                    fontFamily:'inherit', display:'block',
                  }}
                >
                  {expanded ? 'Show less' : 'Read more'}
                </button>
              )}
              {url && <LinkPreview url={url}/>}
            </>
          );
        })()}

        {post.file_deleted_at ? (
          <div style={{
            marginTop:10, padding:'10px 14px', borderRadius:10,
            background:T.s2, border:`1px dashed ${T.bdr}`,
            fontSize:12.5, color:T.mu, fontStyle:'italic',
          }}>
            📎 File removed by author
          </div>
        ) : (post.image_urls?.length > 0) ? (
          <MultiImagePreview urls={post.image_urls}/>
        ) : post.image_url ? (
          <FilePreview url={post.image_url} fileType={post.file_type||'image'} fileName={post.file_name}/>
        ) : null}
        {post.post_type==='paper'&&post.paper_title&&!hidePaperDetails&&<PaperPreview post={post} currentUserId={currentUserId} onViewPaper={onViewPaper} abstractExpanded={abstractExpanded} onToggleAbstract={() => setAbstractExpanded(e => !e)}/>}

        {/* Taxonomy tags — hidden on mobile */}
        {!isMobile && (post.tier2?.length > 0 || post.tags?.length > 0) && !editingTags && (
          <div style={{marginTop:8, paddingTop:6, borderTop:`1px solid ${T.bdr}`}}>
            {post.tier2?.length > 0 && (
              <div style={{display:'flex', gap:4, flexWrap:'wrap', marginBottom:4}}>
                {post.tier2.map(t => (
                  <span key={t} onClick={() => onTagClick && onTagClick(t)}
                    style={{fontSize:10.5, fontWeight:600, padding:'1px 8px', borderRadius:20, background:T.v2, color:T.v, border:`1px solid rgba(108,99,255,.15)`, cursor:onTagClick?'pointer':'default'}}>
                    {t}
                  </span>
                ))}
              </div>
            )}
            {post.tags?.length > 0 && <GranularTags tags={post.tags} onTagClick={onTagClick}/>}
          </div>
        )}

        {/* Inline tag editor */}
        {editingTags && (
          <div style={{background:T.s2, borderRadius:10, padding:12, marginTop:8}}>
            <div style={{fontSize:12, fontWeight:600, marginBottom:6, color:T.mu}}>
              Discipline: {editTier1 || 'Not set'}
            </div>
            <select value={editTier1} onChange={e=>{setEditTier1(e.target.value); setEditTier2([]);}}
              style={{width:'100%', padding:'6px 10px', borderRadius:8, border:`1.5px solid ${T.bdr}`, background:T.w, fontSize:12, fontFamily:'inherit', marginBottom:8, color:T.text}}>
              <option value="">Select discipline…</option>
              {TIER1_LIST.map(t=><option key={t} value={t}>{t}</option>)}
            </select>
            {editTier1 && (
              <div style={{display:'flex', gap:5, flexWrap:'wrap', marginBottom:8}}>
                {getTier2(editTier1).map(t=>(
                  <button key={t} onClick={()=>setEditTier2(prev=>prev.includes(t)?prev.filter(x=>x!==t):prev.length<3?[...prev,t]:prev)}
                    style={{padding:'2px 9px', borderRadius:20, cursor:'pointer', fontSize:11.5, fontFamily:'inherit', border:`1.5px solid ${editTier2.includes(t)?T.v:T.bdr}`, background:editTier2.includes(t)?T.v2:T.w, color:editTier2.includes(t)?T.v:T.text}}>
                    {t}
                  </button>
                ))}
              </div>
            )}
            <div style={{fontSize:11.5, color:T.mu, marginBottom:4}}>Specific tags (comma separated):</div>
            <input value={editTagsText} onChange={e=>setEditTagsText(e.target.value)}
              placeholder="e.g. p53_mutation, CRISPR_cas9"
              style={{width:'100%', padding:'6px 10px', borderRadius:8, border:`1.5px solid ${T.bdr}`, background:T.w, fontSize:12, fontFamily:'inherit', marginBottom:8, color:T.text, boxSizing:'border-box'}}/>
            <div style={{display:'flex', gap:8}}>
              <Btn onClick={()=>setEditingTags(false)}>Cancel</Btn>
              <Btn variant="s" onClick={saveTagEdits}>Save</Btn>
            </div>
          </div>
        )}

        {/* Relevance hook */}
        {isRelevantToUser() && (
          <div style={{
            fontSize: 11.5, color: T.v, fontWeight: 600,
            marginBottom: 8, marginTop: 6,
            display: 'flex', alignItems: 'center', gap: 5,
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill={T.v}>
              <circle cx="12" cy="12" r="10"/>
            </svg>
            {getRelevanceLabel()}
          </div>
        )}

        {/* Action bar */}
        <div style={{display:'flex', alignItems:'center', gap:isMobile?4:8, marginTop:isMobile?6:10, paddingTop:isMobile?8:10, borderTop:`1px solid ${T.bdr}`}}>
          <button onClick={toggleLike} style={{display:'flex', alignItems:'center', gap:4, padding:isMobile?'6px 8px':'8px 10px', border:'none', background:'transparent', cursor:'pointer', color:liked?T.ro:T.mu, fontFamily:'inherit', fontSize:isMobile?12:13}}>
            <svg width={isMobile?15:16} height={isMobile?15:16} viewBox="0 0 24 24" fill={liked?T.ro:'none'} stroke={liked?T.ro:T.mu} strokeWidth="1.8">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
            {(!isMobile || likeCount > 0) && (
              <span
                onClick={e => { if (likeCount > 0) { e.stopPropagation(); setShowLikers(true); } }}
                style={{ cursor: likeCount > 0 ? 'pointer' : 'default' }}
                title={likeCount > 0 ? 'See who liked this' : ''}
              >
                {likeCount}
              </span>
            )}
          </button>

          {/* Comment button with stacked commenter avatars */}
          <button onClick={toggleComments} style={{
            display:'flex', alignItems:'center', gap:5,
            border:'none', background:'transparent',
            cursor:'pointer', padding:isMobile?'6px 6px':'8px 8px',
          }}>
            {!isMobile && commenterAvatars.length > 0 && (
              <div style={{display:'flex', marginRight:2}}>
                {commenterAvatars.map((c, i) => (
                  <div key={c.user_id} style={{
                    marginLeft: i === 0 ? 0 : -8,
                    zIndex: commenterAvatars.length - i,
                    position: 'relative',
                    borderRadius: '50%',
                    border: `1.5px solid ${T.w}`,
                  }}>
                    <Av size={20} color={c.profiles?.avatar_color || T.bdr} name={c.profiles?.name || '?'} url={c.profiles?.avatar_url || ''}/>
                  </div>
                ))}
              </div>
            )}
            <svg width={isMobile?15:16} height={isMobile?15:16} viewBox="0 0 24 24" fill="none" stroke={T.mu} strokeWidth="1.8">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
            {(!isMobile || commCount > 0) && (
              <span style={{fontSize:isMobile?12:13, color:T.mu}}>{commCount}</span>
            )}
          </button>

          <button onClick={toggleRepost} title={reposted?"Undo repost":"Repost to your followers"}
            style={{display:'flex', alignItems:'center', gap:4, padding:isMobile?'6px 8px':'8px 10px', border:'none', background:'transparent', cursor:currentUserId?'pointer':'default', color:reposted?T.gr:T.mu, fontFamily:'inherit', fontSize:isMobile?12:13}}>
            <svg width={isMobile?15:16} height={isMobile?15:16} viewBox="0 0 24 24" fill="none" stroke={reposted?T.gr:T.mu} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 1l4 4-4 4"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/>
              <path d="M7 23l-4-4 4-4"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/>
            </svg>
            {(!isMobile || repostCount > 0) && <span>{repostCount}</span>}
          </button>
          <button onClick={toggleSave} title={saved ? 'Unsave' : 'Save post'}
            style={{display:'flex', alignItems:'center', justifyContent:'center', padding:isMobile?'6px 8px':'8px 10px', border:'none', background:'transparent', cursor:'pointer', color:saved?T.v:T.mu, marginLeft:'auto'}}>
            <svg width={isMobile?14:16} height={isMobile?14:16} viewBox="0 0 24 24"
              fill={saved ? T.v : 'none'} stroke={saved ? T.v : T.mu} strokeWidth="1.8">
              <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
            </svg>
          </button>
          <button onClick={()=>setShowShare(true)} style={{display:'flex', alignItems:'center', gap:4, padding:isMobile?'6px 8px':'8px 10px', border:'none', background:'transparent', cursor:'pointer', color:T.mu, fontFamily:'inherit', fontSize:isMobile?12:13}}>
            <svg width={isMobile?15:16} height={isMobile?15:16} viewBox="0 0 24 24" fill="none" stroke={T.mu} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
              <polyline points="16 6 12 2 8 6"/>
              <line x1="12" y1="2" x2="12" y2="15"/>
            </svg>
            {!isMobile && <span>Share</span>}
          </button>
        </div>

        {/* Top comment preview */}
        {topComment && !showReplyBox && !showComments && (
          <div
            onClick={toggleComments}
            style={{
              marginTop: 10,
              padding: '9px 12px',
              background: T.s2,
              borderRadius: 10,
              border: `1px solid ${T.bdr}`,
              cursor: 'pointer',
              display: 'flex', gap: 8, alignItems: 'flex-start',
            }}
          >
            <Av
              size={24}
              color={topComment.profiles?.avatar_color || T.bdr}
              name={topComment.profiles?.name || '?'}
              url={topComment.profiles?.avatar_url || ''}
            />
            <div style={{flex: 1, minWidth: 0}}>
              <span style={{
                fontSize: 12, fontWeight: 700, marginRight: 5,
                color: topComment.profiles ? T.text : T.mu,
                fontStyle: topComment.profiles ? 'normal' : 'italic',
              }}>
                {topComment.profiles?.name || 'Deleted user'}
              </span>
              <span style={{
                fontSize: 12.5, color: T.text, lineHeight: 1.45,
                overflow: 'hidden', display: '-webkit-box',
                WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
              }}>
                {mentionsToPlainText(htmlToPlain(topComment.content || ''))}
              </span>
            </div>
            {commCount > 1 && (
              <span style={{
                fontSize: 11, color: T.mu, flexShrink: 0,
                alignSelf: 'center',
              }}>
                +{commCount - 1} more
              </span>
            )}
          </div>
        )}

        {/* Quick reply box — collapsed trigger */}
        {!showReplyBox && !showComments && currentUserId && (
          <button
            onClick={() => setShowReplyBox(true)}
            style={{
              width: '100%', marginTop: 8,
              display: 'flex', alignItems: 'center', gap: 8,
              border: `1px solid ${T.bdr}`, borderRadius: 24,
              padding: '7px 12px', background: T.s2,
              cursor: 'pointer', fontFamily: 'inherit',
              textAlign: 'left',
            }}
          >
            <Av size={22} color={currentProfile?.avatar_color}
              name={currentProfile?.name} url={currentProfile?.avatar_url || ''}/>
            <span style={{
              fontSize: 12.5, color: T.mu, flex: 1,
              overflow: 'hidden', textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {commCount === 0
                ? ZERO_COMMENT_PROMPTS[promptIndex % ZERO_COMMENT_PROMPTS.length]
                : getDiscussionPrompts(post.tier1)[promptIndex % getDiscussionPrompts(post.tier1).length]
              }
            </span>
          </button>
        )}

        {/* Quick reply box — expanded */}
        {showReplyBox && !showComments && (
          <div style={{marginTop: 8, display: 'flex', gap: 8, alignItems: 'flex-start'}}>
            <Av size={28} color={currentProfile?.avatar_color}
              name={currentProfile?.name} url={currentProfile?.avatar_url || ''}/>
            <div style={{flex: 1, position: 'relative'}}>
              <textarea
                autoFocus
                value={replyText}
                onChange={e => { setReplyText(e.target.value); handleMentionInput('reply', e.target, e.target.value); }}
                onSelect={e => handleMentionInput('reply', e.target, e.target.value)}
                onBlur={() => setTimeout(() => setMentionState(null), 200)}
                placeholder={
                  commCount === 0
                    ? ZERO_COMMENT_PROMPTS[promptIndex % ZERO_COMMENT_PROMPTS.length]
                    : getDiscussionPrompts(post.tier1)[promptIndex % getDiscussionPrompts(post.tier1).length]
                }
                rows={2}
                style={{
                  width: '100%', fontSize: 13, lineHeight: 1.5,
                  padding: '8px 12px', borderRadius: 12,
                  border: `1.5px solid ${T.v}`, outline: 'none',
                  fontFamily: 'inherit', resize: 'none',
                  background: T.w, boxSizing: 'border-box',
                }}
                onKeyDown={e => {
                  // Don't submit while mention dropdown handles Enter.
                  if (mentionState && mentionState.target === 'reply' && e.key === 'Enter') return;
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitQuickReply(); }
                  if (e.key === 'Escape') { setShowReplyBox(false); setReplyText(''); }
                }}
              />
              <div style={{display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 5}}>
                <Btn onClick={() => { setShowReplyBox(false); setReplyText(''); }}>Cancel</Btn>
                <Btn variant="s" onClick={submitQuickReply} disabled={!replyText.trim()}>Reply</Btn>
              </div>
              {mentionState && mentionState.target === 'reply' && (
                <MentionAutocomplete
                  query={mentionState.query}
                  top={mentionState.top}
                  left={mentionState.left}
                  onSelect={insertMentionInComment}
                  onClose={() => setMentionState(null)}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {showComments&&(
        <div style={{borderTop:`1px solid ${T.bdr}`,background:T.s2}}>
          {commLoading&&<div style={{padding:"14px 16px",fontSize:12.5,color:T.mu}}>Loading comments...</div>}
          {!commLoading&&comments.map(c=>{
            const removed = !c.profiles;
            const isEditing = editingCommentId === c.id;
            return (
            <div key={c.id} style={{display:"flex",gap:10,padding:"12px 16px",borderBottom:`1px solid ${T.bdr}`,background:T.w}}>
              <div onClick={removed?undefined:()=>goToProfile(c.user_id,c.profiles?.profile_slug)} style={{cursor:removed?"default":"pointer",flexShrink:0}}>
                <Av color={c.profiles?.avatar_color||T.bdr} size={30} name={c.profiles?.name||'?'} url={c.profiles?.avatar_url||""}/>
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:"flex",alignItems:"baseline",gap:7,marginBottom:4,flexWrap:"wrap"}}>
                  <span onClick={removed?undefined:()=>goToProfile(c.user_id,c.profiles?.profile_slug)} style={{fontSize:12.5,fontWeight:700,cursor:removed?"default":"pointer",color:removed?T.mu:T.v,fontStyle:removed?"italic":"normal"}}>{c.profiles?.name||"Deleted user"}</span>
                  {c.profiles?.institution&&<span style={{fontSize:10.5,color:T.mu}}>{c.profiles.institution}</span>}
                  <span style={{fontSize:10.5,color:T.mu,marginLeft:"auto"}} title={c.edited_at?`Edited ${new Date(c.edited_at).toLocaleString()}`:undefined}>
                    {timeAgo(c.created_at)}{c.edited_at && <span style={{marginLeft:4,fontStyle:"italic"}}>· edited</span>}
                  </span>
                </div>
                {isEditing ? (
                  <div style={{position:"relative"}}>
                    <textarea
                      value={editingContent}
                      onChange={e=>{setEditingContent(e.target.value); handleMentionInput('edit', e.target, e.target.value);}}
                      onSelect={e=>handleMentionInput('edit', e.target, e.target.value)}
                      onBlur={()=>setTimeout(()=>setMentionState(null), 200)}
                      autoFocus
                      rows={3}
                      style={{width:"100%",padding:"7px 10px",borderRadius:8,border:`1.5px solid ${T.v}`,background:T.w,fontSize:13,fontFamily:"inherit",color:T.text,outline:"none",resize:"vertical",boxSizing:"border-box",lineHeight:1.5}}
                    />
                    {editingError && <div style={{fontSize:11.5,color:T.ro,marginTop:4}}>{editingError}</div>}
                    <div style={{display:"flex",gap:8,marginTop:6,justifyContent:"flex-end"}}>
                      <button onClick={cancelEditComment}
                        style={{fontSize:12,color:T.mu,border:"none",background:"transparent",cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
                      <button onClick={saveEditComment} disabled={editingSaving||!editingContent.trim()}
                        style={{fontSize:12,color:"#fff",background:T.v,border:"none",borderRadius:8,padding:"5px 14px",cursor:editingSaving?"default":"pointer",fontFamily:"inherit",fontWeight:600,opacity:(editingSaving||!editingContent.trim())?0.6:1}}>
                        {editingSaving?"Saving…":"Save"}
                      </button>
                    </div>
                    {mentionState && mentionState.target === 'edit' && (
                      <MentionAutocomplete
                        query={mentionState.query}
                        top={mentionState.top}
                        left={mentionState.left}
                        onSelect={insertMentionInComment}
                        onClose={()=>setMentionState(null)}
                      />
                    )}
                  </div>
                ) : (
                  <div style={{fontSize:13,lineHeight:1.65,color:removed?T.mu:T.text,wordBreak:"break-word",whiteSpace:"pre-wrap"}}><MentionAndLinkify text={c.content}/></div>
                )}
              </div>
              {currentUserId===c.user_id && !isEditing && (
                <div style={{display:"flex",gap:2,flexShrink:0,alignSelf:"flex-start"}}>
                  <button onClick={()=>startEditComment(c)} title="Edit comment"
                    style={{fontSize:12,color:T.mu,border:"none",background:"transparent",cursor:"pointer",opacity:.5,padding:"0 4px"}}>✎</button>
                  <button onClick={()=>deleteComment(c.id)} title="Delete comment"
                    style={{fontSize:12,color:T.mu,border:"none",background:"transparent",cursor:"pointer",opacity:.5,padding:"0 4px"}}>✕</button>
                </div>
              )}
            </div>
            );
          })}
          {!commLoading&&comments.length===0&&commLoaded&&(
            <div style={{padding:"14px 16px",fontSize:12.5,color:T.mu,textAlign:"center",background:T.w}}>No comments yet — be the first to reply.</div>
          )}

          {currentUserId ? (
            <div style={{position:"relative",display:"flex",gap:10,padding:"12px 16px",alignItems:"flex-start",background:T.w,borderTop:`1px solid ${T.bdr}`}}>
              <Av color={currentProfile?.avatar_color||"me"} size={30} name={currentProfile?.name} url={currentProfile?.avatar_url||""}/>
              <div style={{flex:1,display:"flex",gap:8,alignItems:"flex-end"}}>
                <textarea
                  ref={commInputRef}
                  value={commText}
                  onChange={e=>{setCommText(e.target.value); handleMentionInput('new', e.target, e.target.value);}}
                  onSelect={e=>handleMentionInput('new', e.target, e.target.value)}
                  onBlur={()=>setTimeout(()=>setMentionState(null), 200)}
                  onKeyDown={e=>{
                    // Don't submit while mention dropdown handles Enter.
                    if (mentionState && mentionState.target === 'new' && e.key === 'Enter') return;
                    if(e.key==='Enter'&&!e.shiftKey){ e.preventDefault(); submitComment(); }
                  }}
                  placeholder="Write a comment... type @ to mention someone"
                  rows={1}
                  style={{flex:1,background:T.s2,border:`1.5px solid ${T.bdr}`,borderRadius:20,padding:"8px 14px",fontSize:13,fontFamily:"inherit",outline:"none",resize:"none",lineHeight:1.5,color:T.text,minHeight:36,maxHeight:120,overflowY:"auto"}}/>
                <button onClick={submitComment} disabled={commSaving||!commText.trim()}
                  style={{padding:"8px 16px",borderRadius:20,border:"none",background:commText.trim()?T.v:T.bdr,color:commText.trim()?"#fff":T.mu,cursor:commText.trim()?"pointer":"default",fontSize:12,fontFamily:"inherit",fontWeight:700,flexShrink:0,transition:"all .15s"}}>
                  {commSaving?"...":"Reply"}
                </button>
              </div>
              {mentionState && mentionState.target === 'new' && (
                <MentionAutocomplete
                  query={mentionState.query}
                  top={mentionState.top}
                  left={mentionState.left}
                  onSelect={insertMentionInComment}
                  onClose={()=>setMentionState(null)}
                />
              )}
            </div>
          ) : (
            <div style={{padding:"12px 16px",fontSize:12.5,color:T.mu,textAlign:"center",background:T.w}}>Sign in to comment.</div>
          )}
        </div>
      )}

      {showShare && <ShareModal post={post} onClose={()=>setShowShare(false)}/>}
      {showReport && <ReportModal supabase={supabase} postId={post.id} onClose={()=>setShowReport(false)}/>}
      {showLikers && <LikersModal postId={post.id} currentUserId={currentUserId} onClose={()=>setShowLikers(false)}/>}
    </div>
    </div>
  );
}

function menuItemStyle(color) {
  return { display: 'flex', alignItems: 'center', gap: 9, width: '100%', padding: '11px 14px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit', color, textAlign: 'left' };
}
